// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import { ActionIcon, Menu, Text } from '@mantine/core';
import { useLingui } from '@lingui/react';
import { useModals } from '@mantine/modals';
import { IconBallpen, IconCopy, IconDots, IconTag, IconTrash } from '@tabler/icons';
import { Dispatch, FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageTemplateDto } from '../../Types/MessageT';
import { MessageS } from '../../Service/MessageS';
import { openEditGroupModal, openEditTemplateModal } from '../Message/MessageModals';
import { openCopyTemplateModal } from './MessageTemplates/MessageTemplateModals';

interface Props {
  template: MessageTemplateDto;
  existingNames?: string[];
  onDelete?: () => void;
  onUpdate?: (template: MessageTemplateDto) => void;
  groups: string[];
  setTab?: Dispatch<string>;
}

const EmailTemplateActions: FC<Props> = ({ template, onUpdate, onDelete, existingNames, groups, setTab }) => {
  const { name, messageType } = template;
  const navigate = useNavigate();
  const isCustom = messageType === 'CUSTOM';

  const { openConfirmModal } = useModals();
  const { i18n } = useLingui();

  const deleteTemplateById = async () => {
    const deleted = await MessageS.deleteTemplate(name);
    if (deleted) {
      if (onDelete) {
        onDelete();
      } else {
        navigate('/settings/messages');
      }
    }
  };

  const editGroup = () => {
    if (template) {
      openEditGroupModal({
        template,
        existingCategories: groups,
        onConfirm: (val) => {
          if (onUpdate) {
            onUpdate({ ...template, groupName: val });
          }
          if (setTab) {
            setTab(val ?? 'Allgemein');
          }
        },
      });
    }
  };

  const openDeleteModal = () =>
    openConfirmModal({
      title: (
        <Text weight="bolder" size="xl">
          Wirklich löschen?
        </Text>
      ),
      centered: true,
      closeOnConfirm: true,
      children: (
        <Text size="sm">
          Möchtest Du das Vorlage{' '}
          <Text span weight="bolder">
            {name}
          </Text>{' '}
          wirklich löschen?
        </Text>
      ),
      labels: { confirm: i18n._('actions.delete'), cancel: i18n._('actions.cancel') },
      confirmProps: { color: 'red' },
      groupProps: { spacing: 'xs', mt: 'xl' },
      onConfirm: deleteTemplateById,
    });

  return (
    <Menu radius="sm" shadow="md" width={150} transition="scale-y" position="bottom-end">
      <Menu.Target>
        <ActionIcon>
          <IconDots />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        {isCustom && (
          <Menu.Item
            icon={<IconBallpen size={16} />}
            onClick={() =>
              openEditTemplateModal({
                template,
                onFinish:
                  onUpdate ?? ((t: MessageTemplateDto) => navigate(`/settings/template/message/custom/${t.name}`)),
                existingNames: existingNames ?? null,
              })
            }
          >
            Umbenennen
          </Menu.Item>
        )}
        <Menu.Item icon={<IconTag size={16} />} onClick={editGroup}>
          Gruppe bearbeiten
        </Menu.Item>
        <Menu.Item
          icon={<IconCopy size={16} />}
          onClick={() => openCopyTemplateModal({ origin: template, existingNames: existingNames ?? null })}
        >
          Duplizieren
        </Menu.Item>
        {isCustom && (
          <Menu.Item color="red" icon={<IconTrash size={16} />} onClick={openDeleteModal}>
            {i18n._('actions.delete')}
          </Menu.Item>
        )}
      </Menu.Dropdown>
    </Menu>
  );
};

export default EmailTemplateActions;
